import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    KeyRound, Mail, Lock, Loader2, Zap, Shield, Copy, Check,
    ShieldCheck, Users, Landmark, Calculator, Scale, Building2, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === "true";

// 12 usuarios semilla · el orden del grid es 3 col × 4 fila
// group determina el tratamiento visual coordinado con la marca
const DEMO_USERS = [
    { email: "admin_ti@trafalgar.mx",    rol: "Admin TI",             group: "sistema",       Icon: ShieldCheck },
    { email: "director@trafalgar.mx",    rol: "Dirección",            group: "gobernanza",    Icon: Landmark },
    { email: "auditor@trafalgar.mx",     rol: "Auditor / Calif.",     group: "gobernanza",    Icon: Shield },
    { email: "admin_fid@trafalgar.mx",   rol: "Admin. fiduciario",    group: "nucleo",        Icon: Building2 },
    { email: "ops@trafalgar.mx",         rol: "Operaciones",          group: "nucleo",        Icon: Users },
    { email: "tesoreria@trafalgar.mx",   rol: "Tesorería",            group: "nucleo",        Icon: Landmark },
    { email: "tesoreria_2@trafalgar.mx", rol: "Tesorería · segundo",  group: "nucleo",        Icon: Landmark },
    { email: "finanzas@trafalgar.mx",    rol: "Finanzas / Contab.",   group: "finanzas",      Icon: Calculator },
    { email: "juridico@trafalgar.mx",    rol: "Jurídico",             group: "control",       Icon: Scale },
    { email: "cumplimiento@trafalgar.mx",rol: "Cumplimiento / PLD",   group: "control",       Icon: ShieldCheck },
    { email: "riesgos@trafalgar.mx",     rol: "Riesgos / UAIR",       group: "control",       Icon: Zap },
    { email: "parte_externa@lender.com", rol: "Parte externa",        group: "externo",       Icon: ExternalLink },
];

const GROUP_STYLES = {
    // botones color-coded por área
    sistema:    "bg-[color:var(--brand-charcoal)] text-[color:var(--brand-paper)] border-[color:var(--brand-charcoal)] hover:opacity-90",
    gobernanza: "bg-[color:var(--brand-charcoal)]/85 text-[color:var(--brand-paper)] border-[color:var(--brand-charcoal)]/70 hover:opacity-90",
    nucleo:     "bg-[color:var(--brand-gold)] text-[color:var(--brand-charcoal)] border-[color:var(--brand-dark-gold)] hover:brightness-95",
    finanzas:   "bg-[color:var(--brand-paper)] text-[color:var(--brand-charcoal)] border-[color:var(--brand-dark-gold)] hover:bg-[color:var(--brand-dark-gold)]/10",
    control:    "bg-[color:var(--brand-coral)]/90 text-[color:var(--brand-paper)] border-[color:var(--brand-coral)] hover:opacity-90",
    externo:    "bg-transparent text-[color:var(--brand-charcoal)] dark:text-[color:var(--brand-paper)] border-2 border-dashed border-[color:var(--brand-charcoal)]/50 hover:bg-[color:var(--brand-charcoal)]/5",
};

const CREDS_TEXT = `Trafalgar Servicios Fiduciarios — credenciales demo
URL: ${window.location.origin}
Modo demo: activado
Contraseña real: Trafalgar2026! (para los 12 usuarios semilla)
MFA: bypass demo con los 12 botones de acceso rápido en /login
Alternativa: generar TOTP con app autenticadora usando los secrets en /app/memory/test_credentials.md`;


export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, verifyMfa, demoLogin, user } = useAuth();

    const [step, setStep] = useState("credentials"); // credentials | mfa
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [code, setCode] = useState("");
    const [mfaToken, setMfaToken] = useState("");
    const [loading, setLoading] = useState(false);
    const [demoBusyEmail, setDemoBusyEmail] = useState(null);
    const [copied, setCopied] = useState(false);
    const codeRef = useRef(null);

    useEffect(() => {
        if (user) {
            navigate(location.state?.from?.pathname || "/dashboard", { replace: true });
        }
    }, [user, navigate, location]);

    useEffect(() => {
        if (step === "mfa") setTimeout(() => codeRef.current?.focus(), 60);
    }, [step]);

    const onSubmitCreds = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await login(email, password);
            setMfaToken(res.mfa_token);
            setStep("mfa");
            toast.success("Contraseña OK", { description: "Introduce tu código TOTP." });
        } catch (err) {
            toast.error("No se pudo iniciar sesión", {
                description: err?.response?.data?.detail || "Credenciales inválidas",
            });
        } finally {
            setLoading(false);
        }
    };

    const onSubmitMfa = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await verifyMfa(mfaToken, code);
            toast.success("Bienvenido a Trafalgar Servicios Fiduciarios");
            navigate(location.state?.from?.pathname || "/dashboard", { replace: true });
        } catch (err) {
            toast.error("Código inválido", {
                description: err?.response?.data?.detail || "Vuelve a intentarlo",
            });
            setCode("");
        } finally {
            setLoading(false);
        }
    };

    const onDemoClick = async (userSpec) => {
        setDemoBusyEmail(userSpec.email);
        try {
            const data = await demoLogin(userSpec.email);
            toast.success(`Entrando como ${userSpec.rol}`, {
                description: userSpec.email,
            });
            navigate(location.state?.from?.pathname || "/dashboard", { replace: true });
        } catch (err) {
            toast.error("Modo demo no disponible", {
                description: err?.response?.data?.detail || "Verifica DEMO_MODE en backend/.env",
            });
        } finally {
            setDemoBusyEmail(null);
        }
    };

    const onCopyCreds = async () => {
        try {
            await navigator.clipboard.writeText(CREDS_TEXT);
            setCopied(true);
            toast.success("Credenciales copiadas al portapapeles");
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error("No se pudo copiar");
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background">
            {/* Left brand panel */}
            <div
                className="hidden lg:flex flex-col justify-between p-12 relative"
                style={{ backgroundColor: "var(--brand-charcoal)", color: "var(--brand-paper)" }}
            >
                <div className="flex items-center gap-3">
                    <img src="/brand/logo_horizontal_light.png" alt="Trafalgar Servicios Fiduciarios"
                            className="h-16 object-contain" />
                </div>
                <div className="max-w-lg">
                    <h1 className="font-display text-5xl xl:text-6xl leading-[1.05] tracking-tight"
                        style={{ color: "var(--brand-paper)" }}>
                        Administración fiduciaria{" "}
                        <span style={{ color: "var(--brand-gold)" }}>con trazabilidad</span>{" "}
                        y control notarial.
                    </h1>
                    <p className="mt-6 text-base leading-relaxed" style={{ color: "rgba(251,249,245,0.75)" }}>
                        Fideicomisos de garantía, administración, escrow, inversión y planeación
                        patrimonial. Operados con maker-checker riguroso, bitácora criptográfica
                        y reportería institucional.
                    </p>
                </div>
                <div className="space-y-2">
                    <div className="text-[11px] uppercase tracking-widest font-semibold"
                            style={{ color: "var(--brand-gold)" }}>
                        Fiduciario institucional · TRF Casa de Bolsa
                    </div>
                    <div className="text-[10px]" style={{ color: "rgba(251,249,245,0.55)" }}>
                        © 2026 Trafalgar Servicios Fiduciarios · TRF Casa de Bolsa, S.A. de C.V.
                    </div>
                </div>
            </div>

            {/* Right column: form + demo panel */}
            <div className="flex flex-col items-center justify-start p-8 lg:p-12 overflow-y-auto max-h-screen">
                <div className="w-full max-w-md space-y-8">
                    <div className="lg:hidden flex items-center gap-3">
                        <img src="/brand/logo_horizontal_dark.png" alt="Trafalgar Servicios Fiduciarios"
                                className="h-14 object-contain" />
                    </div>

                    {/* Formulario clásico */}
                    <div>
                        <h2 className="font-display text-3xl mb-1">
                            {step === "credentials" ? "Iniciar sesión" : "Verificación MFA"}
                        </h2>
                        <p className="text-sm text-muted-foreground mb-6">
                            {step === "credentials"
                                ? "Accede con tus credenciales corporativas."
                                : "Introduce el código de 6 dígitos de tu app autenticadora."}
                        </p>

                        {step === "credentials" ? (
                            <form onSubmit={onSubmitCreds} className="space-y-4" data-testid="login-form">
                                <div className="space-y-1.5">
                                    <Label htmlFor="email">Correo electrónico</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input id="email" type="email" required autoFocus
                                            autoComplete="username" placeholder="nombre@trafalgar.mx"
                                            value={email} onChange={(e) => setEmail(e.target.value)}
                                            className="pl-9" data-testid="login-email-input" />
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="password">Contraseña</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input id="password" type="password" required
                                            autoComplete="current-password"
                                            value={password} onChange={(e) => setPassword(e.target.value)}
                                            className="pl-9" data-testid="login-password-input" />
                                    </div>
                                </div>
                                <Button type="submit" className="w-full h-11"
                                    disabled={loading || !email || !password}
                                    data-testid="login-submit-button">
                                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continuar"}
                                </Button>
                            </form>
                        ) : (
                            <form onSubmit={onSubmitMfa} className="space-y-4" data-testid="mfa-form">
                                <div className="space-y-1.5">
                                    <Label htmlFor="code">Código TOTP (6 dígitos)</Label>
                                    <div className="relative">
                                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input id="code" ref={codeRef} inputMode="numeric"
                                            pattern="[0-9]{6}" maxLength={6} required
                                            value={code}
                                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                                            className="pl-9 tracking-[0.5em] font-mono text-lg"
                                            data-testid="mfa-code-input" />
                                    </div>
                                    <p className="text-[11px] text-muted-foreground">
                                        Usa tu app autenticadora o los botones de acceso demo debajo.
                                    </p>
                                </div>
                                <Button type="submit" className="w-full h-11"
                                    disabled={loading || code.length !== 6}
                                    data-testid="mfa-submit-button">
                                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verificar y entrar"}
                                </Button>
                                <button type="button"
                                    onClick={() => { setStep("credentials"); setCode(""); }}
                                    className="w-full text-xs text-muted-foreground hover:text-foreground"
                                    data-testid="mfa-back-button">
                                    ← Cambiar cuenta
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Panel demo */}
                    {DEMO_MODE && (
                        <div className="rounded-md border border-dashed border-[color:var(--brand-coral)]/60 bg-[color:var(--brand-coral)]/[0.04] p-4 space-y-4"
                            data-testid="demo-panel">
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-semibold text-[color:var(--brand-coral)]">
                                        <Zap className="h-3 w-3" /> Modo demo activado
                                    </div>
                                    <div className="mt-1 text-sm font-medium">
                                        Acceso directo · un clic por rol
                                    </div>
                                </div>
                                <span className="badge-mock">MOCK</span>
                            </div>
                            <p className="text-[11px] text-[color:var(--brand-coral)] leading-relaxed">
                                ⚠️ Datos poblados con caso <strong>Accial-Trafalgar $60M USD Facility</strong> —{" "}
                                ficticio para demostración. Deshabilitar{" "}
                                <code className="tabular">DEMO_MODE</code> y{" "}
                                <code className="tabular">RESET_SEED_ON_STARTUP</code> en{" "}
                                <code className="tabular">backend/.env</code> antes de producción real.
                                El endpoint <code className="tabular">/api/dev/demo-login</code> registra
                                cada acceso en la bitácora con <code>action=demo_login</code>.
                            </p>

                            <div className="grid grid-cols-3 gap-2" data-testid="demo-buttons-grid">
                                {DEMO_USERS.map((u) => {
                                    const isBusy = demoBusyEmail === u.email;
                                    const Icon = u.Icon;
                                    return (
                                        <button
                                            key={u.email}
                                            type="button"
                                            onClick={() => onDemoClick(u)}
                                            disabled={isBusy || demoBusyEmail !== null}
                                            data-testid={`demo-login-${u.email.split("@")[0]}`}
                                            className={`rounded-sm border px-2 py-2 text-left transition-all disabled:opacity-60 disabled:cursor-not-allowed ${GROUP_STYLES[u.group]}`}
                                        >
                                            <div className="flex items-center gap-1.5">
                                                {isBusy ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <Icon className="h-3.5 w-3.5" />
                                                )}
                                                <div className="text-[11px] font-semibold leading-tight truncate">
                                                    {u.rol}
                                                </div>
                                            </div>
                                            <div className="text-[9px] mt-0.5 opacity-75 truncate font-mono">
                                                {u.email}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="rounded-sm border border-border/60 bg-card p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                                        Compartir con el equipo
                                    </div>
                                    <Button size="sm" variant="ghost" onClick={onCopyCreds}
                                        data-testid="copy-creds-btn"
                                        className="h-6 text-[10px]">
                                        {copied ? (
                                            <><Check className="h-3 w-3 mr-1" /> Copiado</>
                                        ) : (
                                            <><Copy className="h-3 w-3 mr-1" /> Copiar credenciales</>
                                        )}
                                    </Button>
                                </div>
                                <pre className="text-[10px] leading-relaxed font-mono text-muted-foreground whitespace-pre-wrap break-all">
{CREDS_TEXT}
                                </pre>
                            </div>
                        </div>
                    )}

                    <p className="text-[11px] text-muted-foreground border-t border-border pt-4">
                        <span className="badge-mock">MOCK</span> Integraciones externas
                        (firma, PAC, bureau) se etiquetan explícitamente en la UI mientras
                        no exista contrato productivo.
                    </p>
                </div>
            </div>
        </div>
    );
}
